import { Fragment, useState, type ReactNode } from 'react';

import { Spinner } from '../../components/ui/Spinner.js';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const currencyCents = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Formats a 'YYYY-MM' month key as e.g. "Jul 2026". */
export function formatForecastMonth(month: string): string {
  const [year, m] = month.split('-');
  const date = new Date(Date.UTC(Number(year), Number(m) - 1, 1));
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function money(value: string | number, withCents = false): string {
  return (withCents ? currencyCents : currency).format(Number(value));
}

/** One source entity's contribution to a row, with its per-month split (drill-down). */
export interface ForecastGridContribution {
  sourceId: string;
  sourceName: string;
  customerName: string;
  byMonth: Record<string, string>;
  total: number;
}

/** One forecast row (a grouped bucket). */
export interface ForecastGridRow {
  key: string;
  /** First-column content (label / badges). */
  label: ReactNode;
  total: string;
  totalsByMonth: Record<string, string>;
  contributions: ForecastGridContribution[];
}

interface ForecastGridProps {
  title: string;
  subtitle?: string;
  months: string[];
  rows: ForecastGridRow[];
  grandTotal: string;
  totalsByMonth: Record<string, string>;
  /** Opens the source entity (by id) when a drill-down row is clicked. */
  onOpenSource?: (sourceId: string) => void;
  isLoading?: boolean;
  isError?: boolean;
  emptyText: string;
  /** Extra controls rendered in the header (e.g. status toggles). */
  headerExtra?: ReactNode;
  defaultCollapsed?: boolean;
}

const Chevron = ({ open, className = 'h-4 w-4' }: { open: boolean; className?: string }): JSX.Element => (
  <svg
    viewBox="0 0 20 20"
    fill="currentColor"
    className={`${className} text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
  >
    <path
      fillRule="evenodd"
      d="M7.21 4.21a.75.75 0 011.06 0l5 5a.75.75 0 010 1.06l-5 5a.75.75 0 11-1.06-1.06L11.69 10 7.21 5.27a.75.75 0 010-1.06z"
      clipRule="evenodd"
    />
  </svg>
);

/**
 * Shared collapsible, interactive forecast grid: rows are grouped buckets, columns are
 * months, clicking a row drills down to its contributing source entities (each
 * clickable to open). Reused by the agreement + project forecast panels.
 */
export function ForecastGrid({
  title,
  subtitle,
  months,
  rows,
  grandTotal,
  totalsByMonth,
  onOpenSource,
  isLoading,
  isError,
  emptyText,
  headerExtra,
  defaultCollapsed,
}: ForecastGridProps): JSX.Element {
  const [collapsed, setCollapsed] = useState(Boolean(defaultCollapsed));
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleRow = (key: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="mb-4 rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="flex flex-1 items-center gap-2 text-left"
        >
          <Chevron open={!collapsed} />
          <span className="text-sm font-semibold text-slate-900">{title}</span>
          {subtitle ? <span className="text-xs text-slate-500">{subtitle}</span> : null}
        </button>
        <div className="flex items-center gap-3">
          {collapsed ? null : headerExtra}
          <span className="text-sm font-semibold text-slate-900">{money(grandTotal, true)}</span>
        </div>
      </div>

      {collapsed ? null : (
        <div className="border-t border-slate-200">
          {isLoading ? (
            <div className="flex items-center gap-2 px-4 py-6 text-sm text-slate-500">
              <Spinner className="h-4 w-4" /> Loading forecast…
            </div>
          ) : isError ? (
            <div className="px-4 py-6 text-sm text-rose-600">Failed to load the forecast.</div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-6 text-sm text-slate-500">{emptyText}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="sticky left-0 z-10 bg-white px-4 py-2 font-medium">Group</th>
                    {months.map((m) => (
                      <th key={m} className="whitespace-nowrap px-3 py-2 text-right font-medium">
                        {formatForecastMonth(m)}
                      </th>
                    ))}
                    <th className="whitespace-nowrap px-4 py-2 text-right font-medium">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row) => {
                    const isOpen = expanded.has(row.key);
                    return (
                      <Fragment key={row.key}>
                        <tr
                          className="cursor-pointer hover:bg-slate-50"
                          onClick={() => toggleRow(row.key)}
                        >
                          <td className="sticky left-0 z-10 bg-white px-4 py-2 font-medium text-slate-900">
                            <span className="inline-flex items-center gap-1.5">
                              <Chevron open={isOpen} className="h-3.5 w-3.5" />
                              {row.label}
                            </span>
                          </td>
                          {months.map((m) => (
                            <td key={m} className="whitespace-nowrap px-3 py-2 text-right text-slate-600">
                              {row.totalsByMonth[m] ? money(row.totalsByMonth[m]) : '—'}
                            </td>
                          ))}
                          <td className="whitespace-nowrap px-4 py-2 text-right font-semibold text-slate-900">
                            {money(row.total)}
                          </td>
                        </tr>
                        {isOpen
                          ? row.contributions.map((c) => (
                              <tr
                                key={`${row.key}|${c.sourceId}`}
                                className={`bg-slate-50/40 ${onOpenSource ? 'cursor-pointer hover:bg-brand-50' : ''}`}
                                onClick={onOpenSource ? () => onOpenSource(c.sourceId) : undefined}
                              >
                                <td className="sticky left-0 z-10 bg-inherit px-4 py-1.5 pl-9 text-slate-600">
                                  <span className={onOpenSource ? 'font-medium text-brand-700' : 'font-medium text-slate-700'}>
                                    {c.sourceName}
                                  </span>
                                  <span className="text-slate-400"> · {c.customerName}</span>
                                </td>
                                {months.map((m) => (
                                  <td key={m} className="whitespace-nowrap px-3 py-1.5 text-right text-slate-500">
                                    {c.byMonth[m] ? money(c.byMonth[m]) : ''}
                                  </td>
                                ))}
                                <td className="whitespace-nowrap px-4 py-1.5 text-right text-slate-600">
                                  {money(String(c.total))}
                                </td>
                              </tr>
                            ))
                          : null}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot className="border-t-2 border-slate-200 text-sm font-semibold text-slate-900">
                  <tr>
                    <td className="sticky left-0 z-10 bg-white px-4 py-2">Total</td>
                    {months.map((m) => (
                      <td key={m} className="whitespace-nowrap px-3 py-2 text-right">
                        {totalsByMonth[m] ? money(totalsByMonth[m]) : '—'}
                      </td>
                    ))}
                    <td className="whitespace-nowrap px-4 py-2 text-right">{money(grandTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Groups a forecast group's flat contributions into per-source rows with month splits. */
export function groupContributions(
  contributions: { sourceId: string; sourceName: string; customerName: string; month: string; amount: string }[],
): ForecastGridContribution[] {
  const bySource = new Map<string, ForecastGridContribution>();
  for (const c of contributions) {
    let row = bySource.get(c.sourceId);
    if (!row) {
      row = { sourceId: c.sourceId, sourceName: c.sourceName, customerName: c.customerName, byMonth: {}, total: 0 };
      bySource.set(c.sourceId, row);
    }
    row.byMonth[c.month] = c.amount;
    row.total += Number(c.amount);
  }
  return [...bySource.values()].sort((a, b) => b.total - a.total);
}
